import * as assert from "typed-assert";

import { Dispatch } from "redux";

import {
  Amount,
  AuthenticatedAppraisedEmailAddress,
  PaymentFrequency,
  PaymentSourceKind,
  PostalAddress,
  SubmissionState,
  Versioned,
  FormSourceRef,
  Gender,
  IBANValidationResult,
  CustomInputs,
} from "@evergiving/common/dist/es2015/pledge";

import {
  CustomInputTag,
  FreeFormAddressElementType,
} from "@evergiving/common/dist/es2015/form";
import { ColombianBankCodes } from "@evergiving/common/dist/es2015/ValidatorBankAccountCO";
import { PledgePaymentAuthorizationState } from "@/lib/misc/pledgePaymentAuthorization";

// FIXME: duplicated from form.ts because at the time of the implementation, this was an issue for our ts-json-schema-generator
// https://github.com/vega/ts-json-schema-generator/issues/1041
// check issue status and refactor properly (should there be already a PR waiting)
type CustomInputKey = `customInput_${CustomInputTag}`;

export interface VersionedStringFormState
  extends VersionedCustomInputsFormState {
  customAmountString: Versioned<string | undefined>;

  firstName: Versioned<string | undefined>;
  lastName: Versioned<string | undefined>;
  phoneNumber: Versioned<string | undefined>;
  emailAddress: Versioned<string | undefined>;
  countryCode: Versioned<string | undefined>;
  cardHolder: Versioned<string | undefined>;
  cardNumber: Versioned<string | undefined>;
  cardExpiry: Versioned<string | undefined>;
  cardVerificationCode: Versioned<string | undefined>;
  directDebitAccountHolder: Versioned<string | undefined>;
  directDebitAccountNumber: Versioned<string | undefined>;
  directDebitSortCode: Versioned<string | undefined>;
  directDebitIBAN: Versioned<string | undefined>;
  directDebitFinancialInstitution: Versioned<ColombianBankCodes | undefined>;
  directDebitAccountType: Versioned<string | undefined>;
  dateOfBirth: Versioned<string | undefined>;
  gender: Versioned<Gender | undefined>;
  belfioreCode: Versioned<string | undefined>;
  personalIdentificationNumber: Versioned<string | undefined>;
  profession: Versioned<string | undefined>;
  title: Versioned<string | undefined>;
  idTypeSighted: Versioned<string | undefined>;

  [FreeFormAddressElementType.Element3]: Versioned<string | undefined>;
  [FreeFormAddressElementType.Element4]: Versioned<string | undefined>;
  [FreeFormAddressElementType.Element5]: Versioned<string | undefined>;
  [FreeFormAddressElementType.Element6]: Versioned<string | undefined>;
  [FreeFormAddressElementType.Element7]: Versioned<string | undefined>;
  [FreeFormAddressElementType.Element8]: Versioned<string | undefined>;
  [FreeFormAddressElementType.Element9]: Versioned<string | undefined>;
  [FreeFormAddressElementType.Element10]: Versioned<string | undefined>;
  [FreeFormAddressElementType.Element11]: Versioned<string | undefined>;
  [FreeFormAddressElementType.Element12]: Versioned<string | undefined>;
  [FreeFormAddressElementType.Element13]: Versioned<string | undefined>;
  [FreeFormAddressElementType.Element14]: Versioned<string | undefined>;
  [FreeFormAddressElementType.Element15]: Versioned<string | undefined>;

  /**
   * @deprecated Prefer customInputs instead.
   */
  custom1: Versioned<string | undefined>;
}

export type VersionedCustomInputsFormState = {
  [key: CustomInputKey]: Versioned<string | undefined>;
};

export interface BooleanFormState {
  giftAidConsent: true | false | "non-taxpayer";
  termsAndConditionsConsent: boolean;
  phoneContactChecked: boolean;
  smsContactChecked: boolean;
  emailContactChecked: boolean;
  postContactChecked: boolean;
  whatsappContactChecked: boolean;
}

export interface FormState extends VersionedStringFormState, BooleanFormState {
  startDate: string | undefined;
  paymentDay: number | undefined;
}

export interface RootState extends FormState {
  presetAmount?: Amount | null;
  customCurrency?: string;
  paymentFrequency?: PaymentFrequency;
  paymentSourceKind?: PaymentSourceKind;
  postalAddress?: PostalAddress;
  previousSubmissionState?: SubmissionState;
  emailAppraisal: AuthenticatedAppraisedEmailAddress | undefined;
  ibanAppraisal: IBANValidationResult | undefined;
  errors: Partial<{ [key in keyof FormState]: boolean }>;
  formSourceRef: FormSourceRef | undefined;
  formPledgePaymentAuthorization: PledgePaymentAuthorizationState | undefined;
  testingReference: string | undefined;
  customInputs?: CustomInputs;
}

export const SET_PRESET_AMOUNT = "SET_PRESET_AMOUNT";
export const SET_CUSTOM_CURRENCY = "SET_CUSTOM_CURRENCY";
export const SET_PAYMENT_FREQUENCY = "SET_PAYMENT_FREQUENCY";
export const SET_PAYMENT_SOURCE_KIND = "SET_PAYMENT_SOURCE_KIND";
export const SET_PAYMENT_DAY = "SET_PAYMENT_DAY";
export const SET_POSTAL_ADDRESS = "SET_POSTAL_ADDRESS";
export const SET_COUNTRY_CODE = "SET_COUNTRY_CODE";
export const SET_BELFIORE_CODE = "SET_BELFIORE_CODE";
export const SET_START_DATE = "SET_START_DATE";
export const SET_FORM_VALUE = "SET_FORM_VALUE";
export const SET_CUSTOM_INPUT_FORM_VALUE = "SET_CUSTOM_INPUT_FORM_VALUE";
export const RESET_FORM_ERRORS = "RESET_FORM_ERRORS";
export const SET_FORM_ERRORS = "SET_FORM_ERRORS";
export const SET_BOOLEAN_FORM_VALUE = "SET_BOOLEAN_FORM_VALUE";
export const SET_PARTIAL_STATE = "SET_PARTIAL_STATE";
export const SET_FORM_SOURCE_REF = "SET_FORM_SOURCE_REF";
export const SET_FORM_PLEDGE_PAYMENT_AUTHORIZATION =
  "SET_FORM_PLEDGE_PAYMENT_AUTHORIZATION";
export const SET_EMAIL_ADDRESS = "SET_EMAIL_ADDRESS";
export const SET_IBAN = "SET_IBAN";
export const SET_CC_NAME = "SET_CC_NAME";

interface SetPresetAmountAction {
  type: typeof SET_PRESET_AMOUNT;
  payload: {
    amount: Amount | null;
    locale: string;
  };
}

interface SetCustomCurrencyAction {
  type: typeof SET_CUSTOM_CURRENCY;
  payload: string;
}

interface SetPaymentFrequencyAction {
  type: typeof SET_PAYMENT_FREQUENCY;
  payload: PaymentFrequency;
}

interface SetPaymentSourceKindAction {
  type: typeof SET_PAYMENT_SOURCE_KIND;
  payload: PaymentSourceKind;
}

interface SetPaymentDayAction {
  type: typeof SET_PAYMENT_DAY;
  payload: number;
}

interface SetPostalAddressAction {
  type: typeof SET_POSTAL_ADDRESS;
  payload: PostalAddress | undefined;
}

interface SetCountryCodeAction {
  type: typeof SET_COUNTRY_CODE;
  payload: string;
}

interface SetBelfioreCodeAction {
  type: typeof SET_BELFIORE_CODE;
  payload: string;
}

interface SetStartDateAction {
  type: typeof SET_START_DATE;
  payload: string;
}

export interface SetFormValueAction {
  type: typeof SET_FORM_VALUE;
  payload: {
    attribute: keyof VersionedStringFormState;
    value?: string;
    anyErrors: boolean;
  };
}

export interface SetCustomInputFormValueAction {
  type: typeof SET_CUSTOM_INPUT_FORM_VALUE;
  payload: {
    customInputTag: CustomInputTag;
    value: string;
  };
}

interface ResetFormErrorsAction {
  type: typeof RESET_FORM_ERRORS;
  payload: keyof VersionedStringFormState;
}

interface SetFormErrorsAction {
  type: typeof SET_FORM_ERRORS;
  payload: Partial<{ [key in keyof FormState]: boolean }>;
}

interface SetBooleanFormValueAction {
  type: typeof SET_BOOLEAN_FORM_VALUE;
  payload: {
    attribute: keyof BooleanFormState;
    value: boolean;
    anyErrors: boolean;
  };
}

interface SetPartialStateAction {
  type: typeof SET_PARTIAL_STATE;
  payload: Partial<RootState>;
}

interface SetFormSourceRefAction {
  type: typeof SET_FORM_SOURCE_REF;
  payload: FormSourceRef;
}

interface SetFormPledgePaymentAuthorizationAction {
  type: typeof SET_FORM_PLEDGE_PAYMENT_AUTHORIZATION;
  payload: PledgePaymentAuthorizationState;
}

interface EmailAppraisedAction {
  type: typeof SET_EMAIL_ADDRESS;
  payload: {
    emailAddress: string | undefined;
    appraisal: AuthenticatedAppraisedEmailAddress | undefined;
    anyErrors: boolean;
  };
}

interface IbanAppraisedAction {
  type: typeof SET_IBAN;
  payload: {
    iban: string | undefined;
    appraisal: IBANValidationResult | undefined;
    anyErrors: boolean;
  };
}

interface SetCCNameAction {
  type: typeof SET_CC_NAME;
  payload: {
    attribute: "cardHolder" | "directDebitAccountHolder";
    value: string | undefined;
    anyErrors: boolean;
  };
}

export type RootAction =
  | SetPresetAmountAction
  | SetCustomCurrencyAction
  | SetPaymentFrequencyAction
  | SetPaymentSourceKindAction
  | SetPaymentDayAction
  | SetPostalAddressAction
  | SetCountryCodeAction
  | SetBelfioreCodeAction
  | SetStartDateAction
  | SetFormValueAction
  | SetCustomInputFormValueAction
  | SetBooleanFormValueAction
  | ResetFormErrorsAction
  | SetFormErrorsAction
  | SetPartialStateAction
  | SetFormSourceRefAction
  | SetFormPledgePaymentAuthorizationAction
  | EmailAppraisedAction
  | IbanAppraisedAction
  | SetCCNameAction;

export type AppDispatch = Dispatch<RootAction>;

export function assertVersionedString(
  input: Versioned<string | undefined>
): asserts input is Versioned<string> {
  assert.isNotUndefined(input.value);
}

export const noErrors = (() => {
  const result: string[] = [];
  Object.freeze(result);
  return result;
})();
